import { Component, Input, OnChanges } from '@angular/core';
import { AggregateRowModel, ColumnModel, FilterSettingsModel, PageSettingsModel } from '@syncfusion/ej2-angular-grids';
import { inventoryStatusClass } from '../../../models/inventory-status';
import { LotUnitRow, summarizeUnits } from '../lot-unit-summary';

const MOBILE_PAGE_SIZE = 10;

interface MobileRow {
  row: LotUnitRow;
  status?: string;
  statusClass: string;
  detail: string;
}

/** Titled lot summary card: a grid with one column per unit of measure on desktop, compact rows on mobile. */
@Component({
  selector: 'lot-unit-table',
  templateUrl: './lot-unit-table.component.html',
  styleUrls: ['./lot-unit-table.component.scss']
})
export class LotUnitTableComponent implements OnChanges {
  @Input() heading = '';
  @Input() keyHeader = '';
  @Input() rows: LotUnitRow[] = [];
  /** Extra columns after the key column; their values also lead each mobile row's detail line. */
  @Input() leadColumns: ColumnModel[] = [];
  @Input() pageSize = 0;
  @Input() emptyText = 'Nothing on hand for this lot.';

  public columns: ColumnModel[] = [];
  public aggregates: AggregateRowModel[] = [];
  public mobileRows: MobileRow[] = [];
  public mobileVisibleCount = MOBILE_PAGE_SIZE;
  public pageSettings: PageSettingsModel = {};
  public filterSettings: FilterSettingsModel = { mode: 'Immediate', type: 'Excel' };

  ngOnChanges() {
    const summary = summarizeUnits(this.rows ?? []);

    this.columns = [
      { field: 'key', headerText: this.keyHeader, clipMode: 'EllipsisWithTooltip' },
      ...this.leadColumns,
      ...summary.columns
    ];
    this.aggregates = [{
      columns: [{ field: 'key', type: 'Count', footerTemplate: 'Total' }, ...summary.aggregate.columns]
    }];
    this.pageSettings = { pageSize: this.pageSize || 10, pageSizes: [10, 25, 50] };

    const leadFields = this.leadColumns.map(column => column.field).filter(field => field && field !== 'status');
    this.mobileRows = (this.rows ?? []).map(row => ({
      row,
      status: row['status'] as string | undefined,
      statusClass: inventoryStatusClass(row['status'] as string | undefined),
      detail: [
        ...leadFields.map(field => row[field] as string).filter(Boolean),
        ...row.units.map(unit => `${formatQty(unit.value)} ${unit.label}`),
        ...(summary.hasWeight ? [`${formatQty(row.totalWeight)} weight`] : [])
      ].join(' · ')
    }));
    this.mobileVisibleCount = MOBILE_PAGE_SIZE;
  }

  showMoreMobile() {
    this.mobileVisibleCount += MOBILE_PAGE_SIZE;
  }

  trackByRow(_: number, mobileRow: MobileRow): string {
    return mobileRow.row.key;
  }
}

function formatQty(value: number): string {
  return value.toLocaleString('en-US', { maximumFractionDigits: 2 });
}
